import React from 'react';
import Footer from '../components/Footer';
import { 
  PageContainer, 
  Content, 
  Title, 
  Section, 
  SectionTitle, 
  Paragraph, 
  List, 
  ListItem, 
  LastUpdated 
} from '../components/LegalPageLayout';

const PrivacyPolicyPage = () => {
  // ScrollToTop is now handled globally in App.js

  return (
    <PageContainer>
      <Content>
        <Title>Privacy Policy</Title>
        <LastUpdated>Last updated: January 1, 2025</LastUpdated>
        
        <Section>
          <Paragraph>
            At The Sol Room, we are committed to protecting your privacy and personal information. This Privacy Policy 
            explains how we collect, use, and safeguard your information when you use our services, visit our studio, 
            or interact with our website.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Information We Collect</SectionTitle>
          <Paragraph>
            <strong>Personal Information:</strong> We collect information you provide directly, including:
          </Paragraph>
          <List>
            <ListItem>Name, email address, and phone number</ListItem>
            <ListItem>Payment information for class bookings and purchases</ListItem>
            <ListItem>Health information relevant to safe class participation</ListItem>
            <ListItem>Emergency contact details</ListItem>
            <ListItem>Class attendance and booking history</ListItem>
          </List>
          <Paragraph>
            <strong>Automatically Collected Information:</strong> When you visit our website, we may collect:
          </Paragraph>
          <List>
            <ListItem>IP address and browser information</ListItem>
            <ListItem>Website usage patterns and preferences</ListItem>
            <ListItem>Device information and operating system</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>How We Use Your Information</SectionTitle>
          <Paragraph>
            We use your personal information to:
          </Paragraph>
          <List>
            <ListItem>Process class bookings and manage your account</ListItem>
            <ListItem>Communicate about classes, schedules, and studio updates</ListItem>
            <ListItem>Process payments and maintain financial records</ListItem>
            <ListItem>Ensure your safety during classes and activities</ListItem>
            <ListItem>Improve our services and website functionality</ListItem>
            <ListItem>Send promotional materials (with your consent)</ListItem>
            <ListItem>Comply with legal obligations and studio policies</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>Information Sharing</SectionTitle>
          <Paragraph>
            We do not sell, rent, or trade your personal information to third parties. We may share your information only in 
            the following circumstances:
          </Paragraph>
          <List>
            <ListItem><strong>Service Providers:</strong> With trusted third-party services that help us operate our business 
            (booking systems, payment processors, email services)</ListItem>
            <ListItem><strong>Legal Requirements:</strong> When required by law, court order, or to protect our rights and safety</ListItem>
            <ListItem><strong>Emergency Situations:</strong> With emergency contacts or medical personnel if necessary for your safety</ListItem>
            <ListItem><strong>Business Transfers:</strong> In the event of a merger, sale, or transfer of business assets</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>Data Security</SectionTitle>
          <Paragraph>
            We implement appropriate security measures to protect your personal information, including:
          </Paragraph>
          <List>
            <ListItem>Encrypted data transmission and secure servers</ListItem>
            <ListItem>Limited access to personal information by authorized staff only</ListItem>
            <ListItem>Regular security assessments and updates</ListItem>
            <ListItem>Secure payment processing through certified providers</ListItem>
          </List>
          <Paragraph>
            While we strive to protect your information, no method of transmission or storage is 100% secure. 
            We cannot guarantee absolute security but are committed to maintaining industry-standard protections.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Your Rights and Choices</SectionTitle>
          <Paragraph>
            You have the right to:
          </Paragraph>
          <List>
            <ListItem><strong>Access:</strong> Request a copy of the personal information we have about you</ListItem>
            <ListItem><strong>Update:</strong> Correct or update your personal information at any time</ListItem>
            <ListItem><strong>Delete:</strong> Request deletion of your personal information (subject to legal requirements)</ListItem>
            <ListItem><strong>Opt-out:</strong> Unsubscribe from marketing communications</ListItem>
            <ListItem><strong>Restrict:</strong> Limit how we use your information</ListItem>
          </List>
          <Paragraph>
            To exercise these rights, please contact us at help@thesolroom.com or speak with our front desk staff.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Cookies and Website Analytics</SectionTitle>
          <Paragraph>
            Our website uses cookies and similar technologies to enhance your browsing experience, remember your preferences, 
            and analyze website traffic. You can control cookie settings through your browser, though some website 
            functionality may be limited if cookies are disabled.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Third-Party Services</SectionTitle>
          <Paragraph>
            We use third-party services for booking, payments, and communications. These services have their own privacy 
            policies, which we encourage you to review. We are not responsible for the privacy practices of these 
            external services.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Children's Privacy</SectionTitle>
          <Paragraph>
            Our services are intended for adults 18 years and older. We do not knowingly collect personal information 
            from children under 18. If you believe we have collected information from a minor, please contact us immediately.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Changes to This Policy</SectionTitle>
          <Paragraph>
            We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. 
            We will notify you of significant changes by posting the updated policy on our website and indicating the 
            revision date. Your continued use of our services after changes take effect constitutes acceptance of the 
            updated policy.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Contact Us</SectionTitle>
          <Paragraph>
            If you have questions about this Privacy Policy or our data practices, please contact us:
          </Paragraph>
          <Paragraph>
            <strong>Email:</strong> help@thesolroom.com<br/>
            <strong>Phone:</strong> 860-328-6854<br/>
            <strong>Address:</strong> TBA, Chicago, IL
          </Paragraph>
        </Section>
      </Content>
      <Footer />
    </PageContainer>
  );
};

export default PrivacyPolicyPage;